import React, { useState, useRef, FormEvent } from "react"
import "./Auth.scss"
import {
  IonAlert,
  IonButton,
  IonContent,
  IonInput,
  IonItem,
  IonList,
  IonPage,
  IonText,
} from "@ionic/react"
import { login } from "../redux/actions"

import LoginInputControls from "../components/Auth/LoginInputControls"

import logo from "../assets/imgs/logo_login.png"
import API, { AuthData } from "../helpers/API"
import { useDispatch, useSelector } from "react-redux"
import { AppState } from "../global"

const Auth: React.FC = () => {
  const dispatch = useDispatch()
  const userNameRef = useRef<HTMLIonInputElement>(null)
  const passWordRef = useRef<HTMLIonInputElement>(null)
  const appVersion = useSelector((state: AppState) => state.appVersion)
  const [restoreMessage, setRestoreMessage] = useState<string>()
  const [resetStep, setResetStep] = useState<"login" | "request" | "confirm">("login")
  const [resetIdentifier, setResetIdentifier] = useState("")
  const [resetCode, setResetCode] = useState("")
  const [resetPassword, setResetPassword] = useState("")
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState("")
  const [isResetLoading, setIsResetLoading] = useState(false)

  const loginErrorMessage = useSelector(
    (state: AppState) => state.loginErrorMessage
  )

  const [passwordType, setPasswordType] = useState<String>("password")
  const [validationError, setValidationError] = useState<string>("")

  const _login = (event: FormEvent) => {
    event.preventDefault()

    const un = userNameRef.current!.value
    const pw = passWordRef.current!.value

    const authData: AuthData = {
      username: un as string,
      password: pw as string,
    }

    if (!un || !pw) {
      // Se manca roba, ghe lo disemo senza mandar request inutili.
      setValidationError("inserisci email/nome utente e password")
      return
    }
    dispatch(login(authData))
  }

  const togglePasswordType = () => {
    passwordType === "password"
      ? setPasswordType("text")
      : setPasswordType("password")
  }

  const clearRestoreMessage = () => {
    setRestoreMessage("")
  }

  const getPasswordResetError = (error: any) => {
    const data = error?.response?.data
    if (!data) {
      return "Codice non valido o password non accettata."
    }
    if (typeof data === "string") {
      return data
    }
    const firstError = Object.values(data).flat().find(Boolean)
    // Ghe femo vedere el motivo vero, no un messaggio da indovini.
    return firstError?.toString() || "Codice non valido o password non accettata."
  }

  const showResetRequest = () => {
    const currentLogin = (userNameRef.current?.value || "").toString()
    setValidationError("")
    // Recupero solo via email: precompiliamo solo se pare davvero na mail.
    setResetIdentifier(currentLogin.includes("@") ? currentLogin : "")
    setResetStep("request")
  }

  const clearResetSecretFields = () => {
    // I campi segreti restano nello state solo finché serve el reset.
    setResetCode("")
    setResetPassword("")
    setResetPasswordConfirm("")
  }

  const cancelReset = () => {
    setValidationError("")
    clearResetSecretFields()
    setResetStep("login")
  }

  const requestResetCode = async (event: FormEvent) => {
    event.preventDefault()
    const identifier = resetIdentifier.trim()
    if (!identifier) {
      setValidationError("inserisci email")
      return
    }

    setIsResetLoading(true)
    setValidationError("")
    try {
      await API.requestPasswordReset({ email: identifier })
      setRestoreMessage("Se l'utente esiste, riceverai un codice via email.")
      // El codice arriva via mail, ma tenemo l'identificativo pronto per il secondo step.
      setResetIdentifier(identifier)
      clearResetSecretFields()
      setResetStep("confirm")
    } catch (error) {
      setValidationError("Non sono riuscita a richiedere il codice. Riprova tra poco.")
    } finally {
      setIsResetLoading(false)
    }
  }

  const confirmResetCode = async (event: FormEvent) => {
    event.preventDefault()
    const identifier = resetIdentifier.trim()
    const code = resetCode.trim()
    const newPassword = resetPassword
    const confirmedPassword = resetPasswordConfirm

    if (!identifier || !code || !newPassword || !confirmedPassword) {
      setValidationError("compila tutti i campi")
      return
    }

    setIsResetLoading(true)
    setValidationError("")
    try {
      await API.confirmPasswordReset({
        email: identifier,
        code,
        new_password: newPassword,
        confirmed_password: confirmedPassword,
      })
      setRestoreMessage("Password aggiornata. Ora puoi fare login.")
      clearResetSecretFields()
      setResetStep("login")
    } catch (error) {
      setValidationError(getPasswordResetError(error))
    } finally {
      setIsResetLoading(false)
    }
  }

  const renderLoginForm = () => (
    <React.Fragment>
      <form onSubmit={_login}>
        <LoginInputControls
          un={userNameRef}
          pw={passWordRef}
          pwType={passwordType}
          togglePwType={togglePasswordType}
        />

        <IonList className="is-list-buttons is-list-transparent ion-padding ion-no-margin">
          <IonButton type="submit" expand="block">
            Login
          </IonButton>
        </IonList>
      </form>

      <div className="ion-padding-horizontal">
        <p>Hai dimenticato la password?</p>
        <IonButton fill="clear" onClick={showResetRequest}>
          Recupera password
        </IonButton>
      </div>
    </React.Fragment>
  )

  const renderResetRequestForm = () => (
    <form onSubmit={requestResetCode}>
      <IonList className="ion-padding is-list-transparent">
        <IonItem>
          <IonInput
            value={resetIdentifier}
            type="text"
            placeholder="Email"
            onIonChange={(event: any) => setResetIdentifier(event.detail.value || "")}
          ></IonInput>
        </IonItem>
      </IonList>
      <IonList className="is-list-buttons is-list-transparent ion-padding ion-no-margin">
        <IonButton type="submit" expand="block" disabled={isResetLoading}>
          Invia codice
        </IonButton>
        <IonButton fill="clear" expand="block" onClick={cancelReset}>
          Torna al login
        </IonButton>
      </IonList>
    </form>
  )

  const renderResetConfirmForm = () => (
    <form onSubmit={confirmResetCode}>
      <IonList className="ion-padding is-list-transparent">
        <IonItem>
          <IonInput
            value={resetIdentifier}
            type="text"
            placeholder="Email"
            onIonChange={(event: any) => setResetIdentifier(event.detail.value || "")}
          ></IonInput>
        </IonItem>
        <IonItem>
          <IonInput
            value={resetCode}
            type="text"
            placeholder="Codice ricevuto via email"
            onIonChange={(event: any) => setResetCode(event.detail.value || "")}
          ></IonInput>
        </IonItem>
        <IonItem>
          <IonInput
            value={resetPassword}
            type="password"
            placeholder="Nuova password"
            onIonChange={(event: any) => setResetPassword(event.detail.value || "")}
          ></IonInput>
        </IonItem>
        <IonItem>
          <IonInput
            value={resetPasswordConfirm}
            type="password"
            placeholder="Conferma nuova password"
            onIonChange={(event: any) => setResetPasswordConfirm(event.detail.value || "")}
          ></IonInput>
        </IonItem>
      </IonList>
      <IonList className="is-list-buttons is-list-transparent ion-padding ion-no-margin">
        <IonButton type="submit" expand="block" disabled={isResetLoading}>
          Cambia password
        </IonButton>
        <IonButton fill="clear" expand="block" onClick={() => setResetStep("request")}>
          Richiedi un nuovo codice
        </IonButton>
        <IonButton fill="clear" expand="block" onClick={cancelReset}>
          Torna al login
        </IonButton>
      </IonList>
    </form>
  )

  return (
    <IonPage>
      <IonContent className="ion-text-center">
        {restoreMessage && restoreMessage !== "" && (
          <IonAlert
            isOpen={restoreMessage !== ""}
            header=""
            subHeader=""
            message={restoreMessage}
            onDidDismiss={() => clearRestoreMessage()}
            buttons={["OK"]}
          />
        )}

        <div className="is-hero">
          <div className="is-image-container ion-padding-top">
            <img src={logo} alt="iPaddi" />
          </div>
        </div>
        <div style={{ color: "red" }}>{loginErrorMessage}</div>
        {validationError && (
          <IonText color="danger">
            <p>{validationError}</p>
          </IonText>
        )}

        {resetStep === "login" && renderLoginForm()}
        {resetStep === "request" && renderResetRequestForm()}
        {resetStep === "confirm" && renderResetConfirmForm()}

        <div>
          <p>Versione: {appVersion}</p>
        </div>
      </IonContent>
    </IonPage>
  )
}

export default Auth
